import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Search, Mail, Shield, MoreVertical, Trash2, Edit, UserCheck, UserX, ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatDate, getInitials } from '@utils';
import api from '@services/api';
import type { ListResponse } from '@types';

interface TeamMember {
  id: number;
  name: string;
  email: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  roles: Array<{ id: number; name: string }>;
}

const addMemberSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email is required'),
  role: z.string().min(1, 'Role is required'),
});

const editMemberSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  role: z.string().min(1, 'Role is required'),
  is_active: z.boolean(),
});

type AddMemberForm = z.infer<typeof addMemberSchema>;
type EditMemberForm = z.infer<typeof editMemberSchema>;

const roleOptions = [
  { value: 'tenant-admin', label: 'Admin', color: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400' },
  { value: 'reseller', label: 'Manager', color: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400' },
  { value: 'staff', label: 'Staff', color: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' },
];

function getRoleBadge(roleName: string) {
  const role = roleOptions.find(r => r.value === roleName);
  return role || { label: roleName, color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' };
}

export default function TeamSettingsPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data: members, isLoading } = useQuery({
    queryKey: ['team-members', currentPage, searchQuery],
    queryFn: () => api.get<ListResponse<TeamMember>>('/settings/team', {
      page: currentPage,
      search: searchQuery || undefined,
    }),
  });

  const addForm = useForm<AddMemberForm>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { name: '', email: '', role: 'staff' },
  });

  const editForm = useForm<EditMemberForm>({
    resolver: zodResolver(editMemberSchema),
  });

  const createMutation = useMutation({
    mutationFn: (data: AddMemberForm) => api.post('/settings/team', data),
    onSuccess: () => {
      toast.success('Team member invited successfully');
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setShowAddModal(false);
      addForm.reset();
    },
    onError: (error: any) => toast.error(error.message || 'Failed to invite member'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EditMemberForm> }) =>
      api.put(`/settings/team/${id}`, data),
    onSuccess: () => {
      toast.success('Member updated successfully');
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      setEditingMember(null);
    },
    onError: (error: any) => toast.error(error.message || 'Failed to update member'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/settings/team/${id}`),
    onSuccess: () => {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    },
    onError: (error: any) => toast.error(error.message || 'Failed to remove member'),
  });

  const handleDelete = (member: TeamMember) => {
    if (!confirm(`Remove ${member.name} from the team?`)) return;
    deleteMutation.mutate(member.id);
  };

  const handleToggleActive = (member: TeamMember) => {
    updateMutation.mutate({
      id: member.id,
      data: { is_active: !member.isActive, name: member.name, role: member.roles?.[0]?.name || 'staff' },
    });
  };

  const openEditModal = (member: TeamMember) => {
    setEditingMember(member);
    editForm.reset({
      name: member.name,
      role: member.roles?.[0]?.name || 'staff',
      is_active: member.isActive,
    });
    setOpenMenuId(null);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team Settings</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage team members and their roles</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> Invite Member
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search members..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="input pl-10 w-full"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/3 dark:bg-gray-700"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4 dark:bg-gray-700"></div>
                </div>
              </div>
            ))}
          </div>
        ) : members?.data?.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No team members</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Invite your first team member to get started</p>
            <button onClick={() => setShowAddModal(true)} className="btn-primary">
              <Plus className="w-4 h-4" /> Invite Member
            </button>
          </div>
        ) : (
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Member</th>
                <th className="table-header-cell">Role</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Last Login</th>
                <th className="table-header-cell">Joined</th>
                <th className="table-header-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {members?.data?.map((member, index) => {
                const roleName = member.roles?.[0]?.name || 'staff';
                const badge = getRoleBadge(roleName);
                return (
                  <motion.tr
                    key={member.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="table-row"
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-medium text-sm">
                          {getInitials(member.name)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{member.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium', badge.color)}>
                        <Shield className="w-3 h-3" />
                        {badge.label}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                        member.isActive
                          ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      )}>
                        <span className={cn('w-1.5 h-1.5 rounded-full', member.isActive ? 'bg-success-500' : 'bg-gray-400')}></span>
                        {member.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="table-cell text-sm text-gray-500 dark:text-gray-400">
                      {member.lastLoginAt ? formatDate(member.lastLoginAt) : 'Never'}
                    </td>
                    <td className="table-cell text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(member.createdAt)}
                    </td>
                    <td className="table-cell text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                        {openMenuId === member.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)}></div>
                            <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
                              <button
                                onClick={() => openEditModal(member)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <Edit className="w-4 h-4" /> Edit Role
                              </button>
                              <button
                                onClick={() => { handleToggleActive(member); setOpenMenuId(null); }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                {member.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                {member.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                              <hr className="my-1 border-gray-200 dark:border-gray-700" />
                              <button
                                onClick={() => { handleDelete(member); setOpenMenuId(null); }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                              >
                                <Trash2 className="w-4 h-4" /> Remove
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {members?.meta && members.meta.lastPage > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(members.meta.currentPage - 1) * members.meta.perPage + 1} to{' '}
            {Math.min(members.meta.currentPage * members.meta.perPage, members.meta.total)} of{' '}
            {members.meta.total} members
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="btn-secondary p-2">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {members.meta.lastPage}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(members.meta.lastPage, p + 1))} disabled={currentPage === members.meta.lastPage} className="btn-secondary p-2">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowAddModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invite Team Member</h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={addForm.handleSubmit(data => createMutation.mutate(data))} className="p-6 space-y-4">
              <div>
                <label className="label">Name</label>
                <input {...addForm.register('name')} className={cn('input', addForm.formState.errors.name && 'input-error')} placeholder="Full name" />
                {addForm.formState.errors.name && <p className="mt-1 text-sm text-danger-600">{addForm.formState.errors.name.message}</p>}
              </div>
              <div>
                <label className="label">Email</label>
                <input {...addForm.register('email')} type="email" className={cn('input', addForm.formState.errors.email && 'input-error')} placeholder="email@example.com" />
                {addForm.formState.errors.email && <p className="mt-1 text-sm text-danger-600">{addForm.formState.errors.email.message}</p>}
              </div>
              <div>
                <label className="label">Role</label>
                <select {...addForm.register('role')} className="input">
                  {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                A temporary password will be generated. The member can reset it on first login.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={createMutation.isPending} className="btn-primary">
                  {createMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Inviting...</> : 'Send Invite'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditingMember(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Member</h3>
              <button onClick={() => setEditingMember(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={editForm.handleSubmit(data => updateMutation.mutate({ id: editingMember.id, data }))} className="p-6 space-y-4">
              <div>
                <label className="label">Name</label>
                <input {...editForm.register('name')} className="input" />
              </div>
              <div>
                <label className="label">Role</label>
                <select {...editForm.register('role')} className="input">
                  {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" {...editForm.register('is_active')} id="is_active" className="w-4 h-4 rounded border-gray-300 text-primary-600" />
                <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">Active account</label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingMember(null)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={updateMutation.isPending} className="btn-primary">
                  {updateMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : 'Save Changes'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
